import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import styles from "./AddAccountModalComponent.module.css";

/** Slugify a string */
function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Firestore timestamp generator */
function nowTimestamp() {
  const ms = Date.now();
  return {
    type: "firestore/timestamp/1.0",
    seconds: Math.floor(ms / 1000),
    nanoseconds: (ms % 1000) * 1e6,
  };
}

/** Pretty label from key */
function labelFromKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (m) => m.toUpperCase());
}

export default function AddAccountModalComponent({
  onClose,
  onSave,
  accountTypes,
  currencies,
  userEmail,
  userId,
  source = "manual",
}) {
  const [saving, setSaving] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const [form, setForm] = useState({
    name: "",
    currencyCode: "",
    systemTypeId: "",
    balance: "",
    alertThreshold: "",
    systemTypeDesc: "",
    special_attributes: [],
  });

  // Normalize currencies
  const currencyList = useMemo(
    () =>
      (currencies || []).map((c) => ({
        code: c.currencyCode || c.code || "",
        name: c.currencyName || "",
        symbol: c.symbol || "",
      })),
    [currencies]
  );

  const typesById = useMemo(
    () => Object.fromEntries((accountTypes || []).map((t) => [t.id, t])),
    [accountTypes]
  );

  const selectedType = form.systemTypeId ? typesById[form.systemTypeId] : null;

  // When type changes, prepopulate description + special_attributes
  const onTypeChange = (id) => {
    const t = typesById[id];
    setForm((f) => ({
      ...f,
      systemTypeId: id,
      systemTypeDesc: t?.desc || "",
      special_attributes: t?.special_attributes
        ? t.special_attributes.map((sa) => ({ key: sa.key, value: sa.value ?? "" }))
        : [],
    }));
    if (t?.special_attributes?.length) setShowOptional(true);
  };

  const onSpecialAttrChange = (key, value) => {
    setForm((f) => ({
      ...f,
      special_attributes: (f.special_attributes || []).map((sa) =>
        sa.key === key ? { ...sa, value } : sa
      ),
    }));
  };

  const errors = {
    name: !form.name.trim(),
    currencyCode: !form.currencyCode,
    systemTypeId: !form.systemTypeId,
  };
  const isValid = !errors.name && !errors.currencyCode && !errors.systemTypeId;

  const handleSave = async () => {
    if (!isValid) return;

    const ts = nowTimestamp();
    const payload = {
      name: form.name.trim(),
      currencyCode: form.currencyCode,
      slug: slugify(form.name),
      createdAt: ts,
      updatedAt: ts,
      systemTypeId: form.systemTypeId,
      systemTypeName: selectedType?.name || form.systemTypeId,
      systemTypeDesc: form.systemTypeDesc || "",
      accountBalance:
        form.balance === "" || form.balance === null ? 0 : Number(form.balance) || 0,
      special_attributes: form.special_attributes || [],
      userEmail,
      userId,
      source,
    };

    try {
      setSaving(true);
      await onSave?.(payload);
      setSaving(false);
      onClose?.();
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  };

  const handleOverlayMouseDown = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    body.classList.add("modal-open");
    body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      body.classList.remove("modal-open");
      body.style.overflow = prevOverflow || "";
    };
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="modal fade show d-block pp-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onMouseDown={handleOverlayMouseDown}
        style={{ position: "fixed", inset: 0, zIndex: 1080 }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Account</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
            </div>

            <div className="modal-body">
              {/* Required */}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Name *</label>
                  <input
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">Name is required.</div>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label">Currency *</label>
                  <select
                    className={`form-select ${errors.currencyCode ? "is-invalid" : ""}`}
                    value={form.currencyCode}
                    onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}
                  >
                    <option value="">— Select —</option>
                    {currencyList.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                  {errors.currencyCode && (
                    <div className="invalid-feedback">Currency required.</div>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label">Account Type *</label>
                  <select
                    className={`form-select ${errors.systemTypeId ? "is-invalid" : ""}`}
                    value={form.systemTypeId}
                    onChange={(e) => onTypeChange(e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {(accountTypes || []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {errors.systemTypeId && (
                    <div className="invalid-feedback">Account type required.</div>
                  )}
                </div>
              </div>

              {/* Optional fields */}
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowOptional((v) => !v)}
                >
                  {showOptional ? "Hide optional fields" : "Show optional fields"}
                </button>
              </div>

              {showOptional && (
                <div className="row g-3 mt-2">
                  <div className="col-md-4">
                    <label className="form-label">Balance</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.balance}
                      onChange={(e) => setForm({ ...form, balance: e.target.value })}
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label">Type Description</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.systemTypeDesc}
                      onChange={(e) =>
                        setForm({ ...form, systemTypeDesc: e.target.value })
                      }
                    />
                  </div>

                  {form.special_attributes?.length > 0 && (
                    <div className="col-12">
                      <div className="row g-3">
                        {form.special_attributes.map((sa) => (
                          <div className="col-md-4" key={sa.key}>
                            <label className="form-label">
                              {labelFromKey(sa.key)}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={sa.value}
                              onChange={(e) =>
                                onSpecialAttrChange(sa.key, e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !isValid}
              >
                {saving ? "Saving..." : "Add Account"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal-backdrop fade show pp-modal-backdrop"
        style={{ zIndex: 1070 }}
        onMouseDown={onClose}
      />
    </>,
    document.body
  );
}

AddAccountModalComponent.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  accountTypes: PropTypes.array,
  currencies: PropTypes.array,
  userEmail: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  source: PropTypes.string,
};
